// @ts-check

import * as Sentry from "@sentry/react";
import { z } from 'zod';
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getParsedToolParam, cachedParseDirtyJSON as parseDirtyJSON } from './tools';
import { WebSearchResultSchema } from './tools/web-search';
import { GenerateReportArgsSchema, GenerateReportResultSchema } from './tools/generate-report';
import { parseAgentTextResponse } from '@/utils/agent-response';
import { fromMarkdown } from "mdast-util-from-markdown"

const BASE_AGENT_ID = "team"
const BASE_DEEPRESEARCH_AGENT_ID = "research_team_lead"

const QueryEventSchema = z.object({
  event: z.literal('query'),
  data: z.object({
    event: z.union([
      z.literal('RunStarted'),
      z.literal('RunCompleted'),
      z.literal("PlanCreated"),
      z.literal("PlanUpdated"),
      z.literal("PlanCompleted"),
      z.literal("MemoryCleanedUp"),
    ])
  })
});

const RunStartedEventSchema = z.object({
  event: z.literal("RunStarted"),
  data: z.object({
    event: z.literal("RunStarted"),
  })
})

const RunResponseContentEventSchema = z.object({
  event: z.literal('RunResponseContent'),
  data: z.object({
    event: z.literal('RunResponseContent'),
    agent_id: z.string().optional(),
    content: z.string()
  })
});

const RunContentEventSchema = z.object({
  event: z.literal('RunContent'),
  data: z.object({
    event: z.literal('RunContent'),
    agent_id: z.string().optional(),
    content: z.string()
  })
})

const ToolCallStartedEventSchema = z.union([
  z.object({
    event: z.literal('ToolCallStarted'),
    data: z.object({
      event: z.literal('ToolCallStarted'),
      agent_id: z.string(),
      tool_name: z.string(),
      tool_call_id: z.string(),
      args: z.string()
    })
  }),
  z.object({
    event: z.literal('ToolCallStarted'),
    data: z.object({
      event: z.literal('ToolCallStarted'),
      tool_name: z.string(),
      args: z.string()
    })
  }),
  z.object({
    event: z.literal('ToolCallStarted'),
    data: z.object({
      event: z.literal('ToolCallStarted'),
      agent_id: z.string(),
      agent_name: z.string(),
    })
  }),
])

const ToolCallCompletedEventSchema = z.union([
  z.object({
    event: z.literal('ToolCallCompleted'),
    data: z.object({
      event: z.literal('ToolCallCompleted'),
      agent_id: z.string(),
      tool_name: z.string(),
      tool_call_id: z.string(),
      tool_result: z.string()
    })
  }),
  z.object({
    event: z.literal('ToolCallCompleted'),
    data: z.object({
      event: z.literal('ToolCallCompleted'),
      tool_name: z.string(),
      tool_result: z.string()
    })
  }),
  z.object({
    event: z.literal('ToolCallCompleted'),
    data: z.object({
      event: z.literal('ToolCallCompleted'),
      agent_id: z.string(),
      agent_name: z.string(),
    })
  }),
])

const ReasoningContentEventSchema = z.object({
  event: z.literal('ReasoningContent'),
  data: z.object({
    event: z.literal('ReasoningContent'),
    agent_id: z.string().optional(),
    content: z.string(),
  })
});

const ReasoningStartedEventSchema = z.object({
  event: z.literal('ReasoningStarted'),
  data: z.object({
    event: z.literal('ReasoningStarted'),
    agent_id: z.string().optional()
  })
});

const ReasoningStepContentSchema = z.object({
  title: z.string(),
  action: z.string().nullable(),
  result: z.any().nullable(),
  reasoning: z.string(),
  next_action: z.string(),
  confidence: z.number(),
});

const ReasoningStepEventSchema = z.object({
  event: z.literal('ReasoningStep'),
  data: z.object({
    event: z.literal('ReasoningStep'),
    agent_id: z.string(),
    agent_name: z.string(),
    content: ReasoningStepContentSchema,
  })
});

const ReasoningCompletedEventSchema = z.object({
  event: z.literal('ReasoningCompleted'),
  data: z.object({
    event: z.literal('ReasoningCompleted'),
    agent_id: z.string(),
    content: z.object({
      reasoning_steps: z.array(ReasoningStepContentSchema)
    }),
  })
});

const DelegationStartedEventSchema = z.object({
  event: z.literal('DelegationStarted'),
  data: z.object({
    event: z.literal('DelegationStarted'),
    agent_id: z.string(),
    task: z.string(),
    message: z.string(),
  })
});

const DelegationCompletedEventSchema = z.object({
  event: z.literal('DelegationCompleted'),
  data: z.object({
    event: z.literal('DelegationCompleted'),
    agent_id: z.string(),
    result: z.string(),
  })
});

const SubAgentStartedEventSchema = z.object({
  event: z.literal('SubAgentStarted'),
  data: z.object({
    event: z.literal('SubAgentStarted'),
    agent_id: z.string(),
    agent_name: z.string(),
    task: z.string(),
  })
})

const SubAgentCompletedEventSchema = z.object({
  event: z.literal('SubAgentCompleted'),
  data: z.object({
    event: z.literal('SubAgentCompleted'),
    agent_id: z.string(),
    agent_name: z.string(),
  })
})

const SubAgentRunStartedEventSchema = z.object({
  event: z.literal('SubAgentRunStarted'),
  data: z.object({
    event: z.literal('SubAgentRunStarted'),
    agent_id: z.string(),
  })
})

const SubAgentRunCompletedEventSchema = z.object({
  event: z.literal('SubAgentRunCompleted'),
  data: z.object({
    event: z.literal('SubAgentRunCompleted'),
    agent_id: z.string(),
  })
})

const ResearchCompleteEventSchema = z.object({
  event: z.literal('research_complete'),
  data: z.object({
    event: z.literal('research_complete'),
    chat: z.object({}).passthrough(),
  })
});

const RunCompletedEventSchema = z.object({
  event: z.literal('RunCompleted'),
  data: z.object({
    event: z.literal('RunCompleted'),
  })
})

const ChatMessagePartSchema = z.union([
  QueryEventSchema,
  RunStartedEventSchema,
  RunResponseContentEventSchema,
  RunContentEventSchema,
  ToolCallStartedEventSchema,
  ToolCallCompletedEventSchema,
  ReasoningContentEventSchema,
  ReasoningStartedEventSchema,
  ReasoningStepEventSchema,
  ReasoningCompletedEventSchema,
  DelegationStartedEventSchema,
  DelegationCompletedEventSchema,
  SubAgentStartedEventSchema,
  SubAgentCompletedEventSchema,
  SubAgentRunStartedEventSchema,
  SubAgentRunCompletedEventSchema,
  ResearchCompleteEventSchema,
  RunCompletedEventSchema,
]);
/** @typedef {typeof ChatMessagePartSchema} ChatMessagePartSchemaType */

const TOOLS_IGNORE_LIST = [
  "think",
  "create",
  "create_agent",
  "str_replace",
  "delegate_task",
  "delegate_tasks_parallel",
  "analyze",
  "wait_for_agents",
  "view",
  "get_all_citations",
  "Delegate Task To Member",
  "Delegate Task to Member",
]

/** @param {ChatMessagePart[]} parts */
function normaliseChatMessageParts(parts) {
  /** @type {NormalisedChatMessagePart<keyof NormalisedChatMessagePartDataMap>[]} */
  const normalisedParts = []

  const consumed = new Set()

  let i = 0;
  while (i < parts.length) {
    if (consumed.has(i)) { i++; continue }
    let part
    try {
      part = ChatMessagePartSchema.parse(parts[i]);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'chat-bubble utils - parse chat message part',
          partIndex: i,
          totalParts: parts.length,
          partData: parts[i]
        }
      });
      console.error(
        new Error(error?.message, {
          cause: {
            error,
            part: parts[i]
          }
        })
      )
    }
    if (part == null) {
      i++; continue
    }

    // Skip query events
    if (part.event === 'query') {
      i++;
      continue;
    }

    // Handle RunResponseContent - combine adjacent ones
    if (part.event === 'RunResponseContent' || part.event === 'RunContent') {
      let combinedContent = part.data.content;
      let j = i + 1;

      // Look ahead for more RunResponseContent events
      while (j < parts.length && (parts[j].event === 'RunResponseContent' || parts[j].event === 'RunContent') && parts[j].data.agent_id === part.data.agent_id) {
        combinedContent += parts[j].data.content;
        j++;
      }

      // Only add if there's actual content
      if (combinedContent.length > 0) {
        for (const textPart of parseAgentTextResponse(combinedContent)) {
          if (textPart.type === 'raw_text') {
            normalisedParts.push({
              type: 'text',
              agent_id: part.data.agent_id,
              data: { text: textPart.content }
            })
          } else {
            normalisedParts.push({
              type: textPart.type,
              agent_id: part.data.agent_id,
              data: { content: textPart.content }
            })
          }
        }
      }

      i = j;
      continue;
    }

    if (part.event === "ReasoningContent") {
      let combinedContent = part.data.content;
      consumed.add(i);

      for (let j = i + 1; j < parts.length; j++) {
        const candidate = /** @type {any} */ (parts[j]);
        const candidateAgentId = candidate.data?.agent_id;
        const isSameAgent = candidateAgentId === part.data.agent_id;

        if (candidate.event === 'ReasoningContent') {
          if (isSameAgent) {
            combinedContent += candidate.data.content;
            consumed.add(j);
          }
          // Different agent's ReasoningContent — jump over, keep scanning
        } else if (isSameAgent) {
          // Non-ReasoningContent event from the same agent — boundary, stop scanning.
          // Another agent doing a tool call or emitting content is not a boundary for us.
          break;
        }
      }

      normalisedParts.push({
        type: 'reasoning_content',
        agent_id: part.data.agent_id,
        data: { content: combinedContent }
      })

      i++;
      continue;
    }

    if (part.event === "ReasoningStep") {
      normalisedParts.push({
        type: "reasoning_step",
        agent_id: part.data.agent_id,
        data: {
          title: part.data.content.title,
          content: part.data.content.reasoning
        }
      })
    }

    // Handle ToolCallStarted - only check the next part for completion
    if (part.event === 'ToolCallStarted') {
      const toolName = part.data.tool_name;
      const args = part.data.args;

      if (toolName == null) {
        i++; continue
      }

      if (TOOLS_IGNORE_LIST.includes(toolName)) {
        i++; continue
      }

      if (part.data.tool_call_id != null) {
        let hasCompleted = false
        for (let j = i + 1; j < parts.length; j++) {
          const nextPart = parts[j]
          if (nextPart.event !== "ToolCallCompleted") continue
          if (nextPart.data.tool_call_id !== part.data.tool_call_id) continue

          let toolResult = nextPart.data.tool_result

          normalisedParts.push({
            type: 'tool_call',
            agent_id: part.data.agent_id,
            data: {
              name: toolName,
              args: args,
              result: toolResult
            }
          })

          hasCompleted = true
          break
        }

        if (!hasCompleted) {
          normalisedParts.push({
            type: 'tool_call',
            agent_id: part.data.agent_id,
            data: {
              name: toolName,
              args: args,
              result: undefined
            }
          })
        }

        i++
        // check the immediate next part for ToolCallCompleted
      } else if (
        i + 1 < parts.length &&
        parts[i + 1].event === 'ToolCallCompleted' &&
        /** @type {any} */ (parts[i + 1]).data.tool_name === toolName &&
        /** @type {any} */ (parts[i + 1]).data.agent_id === part.data.agent_id
      ) {
        let toolResult = /** @type {any} */ (parts[i + 1]).data.tool_result;

        normalisedParts.push({
          type: 'tool_call',
          agent_id: part.data.agent_id,
          data: {
            name: toolName,
            args: args,
            result: toolResult
          }
        });

        // Skip both ToolCallStarted and ToolCallCompleted
        i += 2;
      } else {
        // No immediate completion found, add without result
        normalisedParts.push({
          type: 'tool_call',
          agent_id: part.data.agent_id,
          data: {
            name: toolName,
            args: args,
            result: undefined
          }
        });

        i++;
      }
      continue;
    }

    if (part.event === "SubAgentStarted") {
      let completedPart = null
      for (let j = i + 1; j < parts.length; j++) {
        const nextPart = parts[j]
        if (nextPart.event === "SubAgentCompleted" && nextPart.data.agent_id === part.data.agent_id) {
          completedPart = nextPart
          break
        }
      }

      normalisedParts.push({
        type: "sub_agent",
        agent_id: part.data.agent_id,
        data: {
          task: part.data.task,
          status: completedPart !== null ? "completed" : "pending"
        }
      })
    }

    // Error handling for orphaned ToolCallCompleted events
    // if (part.event === 'ToolCallCompleted') {
    //   throw new Error(`Orphaned ToolCallCompleted event found for tool: ${/** @type {any} */ (part).data.tool_name}. ToolCallCompleted events must immediately follow their corresponding ToolCallStarted event.`);
    // }

    i++;
  }

  // console.log({parts, normalisedParts})
  return normalisedParts;
}

/**
 * @template {keyof NormalisedChatMessagePartDataMap} T
 * @typedef {Object} NormalisedChatMessagePart
 * @property {T} type
 * @property {string} [agent_id]
 * @property {NormalisedChatMessagePartDataMap[T]} data
 */

/**
 * @typedef {Object} NormalisedChatMessagePartDataMap
 * @property {{text: string}} text
 * @property {{content: string}} reasoning_tag
 * @property {{content: string}} reasoning_content
 * @property {{content: string}} report_generation_tag
 * @property {{name: string, args: string, result: string | null}} tool_call
 * @property {{title: string, content: string}} reasoning_step
 * @property {{task: string, status: "pending" | "completed"}} sub_agent
 */

/**
 * @param {NormalisedChatMessagePart<keyof NormalisedChatMessagePartDataMap>[]} parts
 * @returns {z.infer<typeof WebSearchResultSchema>["citations"]}
 */
function getAllMessageCitations(parts) {
  return parts
    .filter(isToolCallPart)
    .filter(part => part.data.name === "web_search")
    .map(part => {
      try {
        const result = WebSearchResultSchema.parse(
          parseDirtyJSON(part.data.result)
        )
        return result.citations
      } catch (error) {
        const resultData = (() => {
          try {
            return part.data.result?.substring(0, 200)
          } catch (error) {
            Sentry.captureException(error, {
              extra: {
                context: 'chat-bubble utils - parse web search citations for error reporting',
                partData: part.data,
              }
            });
            return `Could not parse result data; typeof result: ${typeof part.data.result}`
          }
        })()

        Sentry.captureException(error, {
          extra: {
            context: 'chat-bubble utils - parse web search citations',
            toolName: part.data.name,
            resultData,
          }
        });
        return null
      }
    })
    .filter(Boolean)
    .flat()
}

/**
 * @param {NormalisedChatMessagePart<keyof NormalisedChatMessagePartDataMap>} part
 * @returns {part is NormalisedChatMessagePart<"tool_call">}
 */
function isToolCallPart(part) {
  return part.type === "tool_call"
}

/**
 * @param {NormalisedChatMessagePart<keyof NormalisedChatMessagePartDataMap>[]} parts
 * @returns {z.infer<typeof WebSearchResultSchema>["images"]}
 */
function getAllMessageSearchImageResults(parts) {
  const images = parts
    .filter((part) => part.type === "tool_call")
    .filter(part => part.data.name === "web_search")
    .map(part => part.data.result)
    .filter(Boolean)
    .map(result => {
      try {
        return parseDirtyJSON(result)
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: 'chat-bubble utils - parse web search images',
            result: result,
          }
        });
        return {}
      }
    })
    .map(result => result.images)
    .filter(Boolean)
    .filter(images => Array.isArray(images)) // Handles cases where images is "None"
    .flat()

  const seen = new Set();
  return images.filter(img => {
    const url = img.original || img.link || img.thumbnail;
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

/**
 * Deduplicates citations by URL and returns the unique citations and an index map.
 * @param {import("@/utils/citations").Citation[]} citations
 * @returns {{ citations: import("@/utils/citations").Citation[], indexMap: Map<number, number> }}
 */
function deduplicateCitations(citations) {
  const uniqueCitations = [];
  const urlToNewIndex = new Map();
  const oldToNewIndex = new Map();

  citations.forEach((citation, index) => {
    const oldIndex = index + 1;
    if (urlToNewIndex.has(citation.url)) {
      oldToNewIndex.set(oldIndex, urlToNewIndex.get(citation.url));
    } else {
      const newIndex = uniqueCitations.length + 1;
      uniqueCitations.push(citation);
      urlToNewIndex.set(citation.url, newIndex);
      oldToNewIndex.set(oldIndex, newIndex);
    }
  });

  return { citations: uniqueCitations, indexMap: oldToNewIndex };
}

/**
 * Remaps citation numbers in text using the provided index map.
 * @param {string} text
 * @param {Map<number, number>} indexMap
 * @returns {string}
 */
function remapCitationsInText(text, indexMap) {
  if (!text || indexMap.size === 0) return text;
  return text.replace(/\[(\d+(?:\s*,\s*\d+)*)\]/g, (match, p1) => {
    const numbers = p1.split(',').map(n => n.trim());
    const remapped = numbers.map(n => indexMap.get(Number(n)) || n);
    const uniqueRemapped = [...new Set(remapped)];
    return `[${uniqueRemapped.join(', ')}]`;
  });
}

/**
 * @param {NormalisedChatMessagePart<keyof NormalisedChatMessagePartDataMap>[]} parts
 * @returns {(z.infer<typeof GenerateReportResultSchema> & z.infer<typeof GenerateReportArgsSchema>)[]}
 */
function getAllMessageReports(parts) {
  const reportParts = parts
    .filter((part) => part.type === "tool_call")
    .filter(part => part.data.name === "generate_report")
    .map(part => part.data)
    .filter(Boolean)
  // .map(result => parseDirtyJSON(result))

  /** @type {(z.infer<typeof GenerateReportResultSchema> & z.infer<typeof GenerateReportArgsSchema>)[]} */
  let successReportParts = []

  for (let i = 0; i < reportParts.length; i++) {
    let part = reportParts[i]
    try {
      let parsedReportResult = getParsedToolParam(part.result, GenerateReportResultSchema)
      let parsedReportArgs = getParsedToolParam(part.args, GenerateReportArgsSchema)
      if (parsedReportArgs == null || parsedReportArgs instanceof Error || parsedReportResult == null || parsedReportResult instanceof Error) continue
      // @ts-ignore
      successReportParts.push({ ...parsedReportArgs, ...parsedReportResult })
    } catch (error) {
      continue
    }
  }

  return successReportParts
}

const ToolGeneratedFilesKeyMap = {
  "Calculate Properties with ADMET AI and RDKit": ["storage_url"],
  "Calculate Physicochemical Properties with ADMET AI": ["storage_url"],
  "Perform Molecular Docking with AutoDock Vina": ["poses_storage_url", "receptor_storage_url", "ligand_storage_url"],
  "Dock with Vina": ["poses_storage_url", "receptor_storage_url", "ligand_storage_url"],
  "Generate 3d Structure with RDKit and Py3Dmol": ["storage_url"],
  "Optimize 3d Structure with RDKit": ["storage_url"],
  "Convert Chemical Format with OpenBabel": ["storage_url"],

  "Predict Glycosylation Sites with GlycoEP": ["storage_url"],
  "Ppredict Bcell Epitopes with ABCpred": ["storage_url"],
  "Predict IEDB Epitopes with IEDB Epitope": ["storage_url"],
  "Predict Protein Structure with SWISS Model": ["storage_url"],
  "Predict Binding Affinity with Prodigy": ["receptor_storage_url", "ligand_storage_url", "complex_storage_url"],
  "Analyze IgBLAST with IgBLAST": ["storage_url"],
  "Comprehensive Analysis": [/^.+_storage_url$/, /^analyses\..+\.storage_url$/]
};

// Maps tool names to display-friendly category names
const ToolCategoryMap = {
  "Calculate Properties with ADMET AI and RDKit": "ADMET Analysis",
  "Calculate Physicochemical Properties with ADMET AI": "ADMET Analysis",
  "Perform Molecular Docking with AutoDock Vina": "Docking Run",
  "Dock with Vina": "Docking Run",
  "Generate 3d Structure with RDKit and Py3Dmol": "3D Structure",
  "Optimize 3d Structure with RDKit": "3D Structure",
  "Convert Chemical Format with OpenBabel": "Format Conversion",
  "Predict Glycosylation Sites with GlycoEP": "Glycosylation Prediction",
  "Ppredict Bcell Epitopes with ABCpred": "Bcell Epitope Prediction",
  "Predict IEDB Epitopes with IEDB Epitope": "IEDB Epitope Prediction",
  "Predict Protein Structure with SWISS Model": "Protein Structure",
  "Predict Binding Affinity with Prodigy": "Binding Affinity",
  "Analyze IgBLAST with IgBLAST": "IgBLAST Analysis",
  "Comprehensive Analysis": "Comprehensive Analysis"
};

// Returns only the pathname component of a URL, stripping query string and fragment.
function getUrlPathname(url) {
  try {
    return new URL(url).pathname;
  } catch {
    return url.replace(/^https?:\/\/[^/?#]*/, '').replace(/[?#].*$/, '');
  }
}

// File extensions we surface as downloadable artifacts.
// Protein/molecular structure formats + common data formats.
const SUPPORTED_ARTIFACT_EXTENSIONS = new Set([
  'pdb', 'pdbqt', 'cif', 'mmcif', 'mol2', 'sdf',
  'txt', 'json', 'xml', 'csv',
]);

// Returns true when the URL's path ends with one of the supported artifact extensions.
function isSupportedArtifactUrl(url) {
  try {
    const pathname = getUrlPathname(url);
    const filename = pathname.split('/').filter(Boolean).pop() || '';
    const dotIdx = filename.lastIndexOf('.');
    if (dotIdx === -1) return false;
    const ext = filename.slice(dotIdx + 1).toLowerCase();
    return SUPPORTED_ARTIFACT_EXTENSIONS.has(ext);
  } catch {
    return false;
  }
}

// Helper to extract unique run ID from URL
function extractRunIdFromUrl(url) {
  if (!url) return null;
  try {
    const parts = getUrlPathname(url).split('/').filter(Boolean);
    const idSegment = parts[parts.length - 2];
    if (idSegment && idSegment.length >= 6) {
      return idSegment.slice(-6);
    }
  } catch (e) { }
  return null;
}

// Helper to extract hash from receptor filename
function extractReceptorHash(url) {
  if (!url) return null;
  try {
    const filename = getUrlPathname(url).split('/').pop();
    const match = filename?.match(/receptor_([a-f0-9]+)_/i);
    if (match?.[1]) return match[1];
  } catch (e) { }
  return null;
}

/**
 * @param {NormalisedChatMessagePart<keyof NormalisedChatMessagePartDataMap>[]} parts
 */
function getAllMessageGeneratedFiles(parts) {
  return parts
    .filter(part => part.type === "tool_call")
    .filter(part => part.data.result != null && !(part.data.result instanceof Error))
    .filter(part => {
      if (part.data.name in ToolGeneratedFilesKeyMap) return true;
      // Also include tool calls that carry artifact_urls in compact-mode responses
      try {
        const r = parseDirtyJSON(part.data.result);
        return r?.artifact_urls != null && Object.keys(r.artifact_urls).length > 0;
      } catch { return false; }
    })
    .map((part, partIdx) => {
      if (part.type !== "tool_call") return null
      try {
        const result = parseDirtyJSON(part.data.result)
        const args = parseDirtyJSON(part.data.args || "{}")
        const fileKeys = ToolGeneratedFilesKeyMap[part.data.name]

        // Compact-mode responses lift artifact URLs into a flat top-level artifact_urls map
        const artifactUrls = (result.artifact_urls != null && typeof result.artifact_urls === 'object')
          ? result.artifact_urls
          : {};

        const files = []

        // Helper to add file with determining type
        const addFile = (fileUrl, keyName) => {
          if (!fileUrl || typeof fileUrl !== 'string') return;
        
          if (files.some(f => f.url === fileUrl)) return;
          let type = 'other';
          if (keyName.includes('receptor')) type = 'receptor';
          else if (keyName.includes('ligand')) type = 'ligand';
          else if (keyName.includes('poses') || part.data.name.toLowerCase().includes('dock')) type = 'docking_result';
          // Fallback heuristics based on tool name if key is generic 'storage_url'
          else if (part.data.name.includes('Structure') || part.data.name.includes('Model')) type = 'receptor';

          let distinctName = null;
          if (result.filename) distinctName = result.filename;
          else if (result.name) distinctName = result.name;
          else if (result.output_name) distinctName = result.output_name;
          else if (keyName.includes('receptor')) {
            distinctName = result.receptor_name || args.receptor_name || args.receptor || null;
          }
          else if (keyName.includes('ligand')) {
            distinctName = result.ligand_name || args.ligand_name || args.ligand || null;
          }
          else if (keyName.includes('poses') || part.data.name.toLowerCase().includes('dock')) {
            distinctName = result.output_name || result.run_name || args.output_name || null;
          }

          files.push({
            url: fileUrl,
            type: type,
            toolName: part.data.name,
            toolCategory: ToolCategoryMap[part.data.name] || 'Other',
            extractedName: distinctName,
            toolCallId: partIdx,
            runId: extractReceptorHash(fileUrl) || extractRunIdFromUrl(fileUrl),
            projectName: result.project_name || result.run_name || result.job_name || null
          });
        };

        // --- Old path (backward compat): extract via ToolGeneratedFilesKeyMap ---
        if (fileKeys != null) {
          for (const key of fileKeys) {
            if (typeof key === "string" && key in result) {
              addFile(result[key], key);
            }
            // Handle nested structures: conformers[].storage_url, etc.
            if (typeof key === "string" && !(key in result)) {
              // Check inside conformers array (e.g., Generate 3D Structure tool)
              if (Array.isArray(result.conformers)) {
                result.conformers.forEach((conf) => {
                  if (conf && conf[key]) {
                    addFile(conf[key], key);
                  }
                  // Also check with underscored/spaced variants
                  const conformerSpacedKey = key.replace(/_/g, ' ');
                  if (conf && conf[conformerSpacedKey]) {
                    addFile(conf[conformerSpacedKey], key);
                  }
                });
              }
              // Check with space-separated key variant (dirty JSON may convert underscores to spaces)
              const spacedKey = key.replace(/_/g, ' ');
              if (spacedKey in result) {
                addFile(result[spacedKey], key);
              }
            }
            if (key instanceof RegExp) {
              for (const resultKey of Object.keys(result)) {
                if (!key.test(resultKey)) continue
                addFile(result[resultKey], resultKey);
              }
            }
          }
        }

        // --- New path: extract from artifact_urls (compact-mode responses) ---
        // Only include URLs whose path ends with a file extension — artifact_urls can
        // also contain viewer pages and API endpoints that aren't downloadable files.
        for (const [key, url] of Object.entries(artifactUrls)) {
          if (isSupportedArtifactUrl(url)) addFile(url, key);
        }

        return files
      } catch (error) {
        console.error(error)
        return null
      }
    })
    .filter(Boolean)
    .flat(Infinity)
}

/**
 * @typedef {ReturnType<typeof getAllMessageReports>} AgentMessageReport
 */

/** @typedef {ReturnType<typeof fromMarkdown>} MdRoot */
/** @typedef {MdRoot["children"] extends (infer U)[] ? U: never} MdRootContent */
/** @typedef {MdRoot | MdRootContent} MdNode */

/**
 * Formats markdown text for copying by stripping most formatting but preserving
 * links as "text (url)" and maintaining list structure with proper numbering/bullets
 * @param {string} markdownText - The markdown text to format
 * @returns {string} - Formatted plain text suitable for copying
 */
function formatMarkdownForCopy(markdownText) {
  const tree = fromMarkdown(markdownText);
  return processMarkdownNodeForCopy(tree);
}

/**
 * Renders markdown text to an HTML string suitable for the clipboard's
 * "text/html" payload, so pasting into rich text editors preserves
 * formatting (bold, lists, headings, tables, etc.) the same way native
 * browser copy-from-selection does.
 * @param {string} markdownText - The markdown text to format
 * @returns {string} - HTML markup
 */
function formatMarkdownForCopyHtml(markdownText) {
  return renderToStaticMarkup(
    React.createElement(Markdown, { remarkPlugins: [remarkGfm] }, markdownText)
  );
}

/**
 * @param {MdNode} node
 * @returns {string}
 */
function processMarkdownNodeForCopy(node) {
  switch (node.type) {
    case "root":
      return joinTextList(processMarkdownNodeListForCopy(node.children), "\n\n");

    case "heading":
      return joinTextList(processMarkdownNodeListForCopy(node.children));

    case "paragraph":
      return joinTextList(processMarkdownNodeListForCopy(node.children));

    case "link":
      const linkText = joinTextList(processMarkdownNodeListForCopy(node.children));
      return linkText ? `${linkText}(${node.url})` : node.url;

    case "inlineCode":
    case "code":
      return node.value;

    case "emphasis":
    case "strong":
      return joinTextList(processMarkdownNodeListForCopy(node.children));

    case "text":
      return node.value;

    case "html":
      if (node.value.toLowerCase().includes('<br')) {
        const brCount = (node.value.match(/<br\s*\/?>/gi) || []).length;
        return "\n".repeat(brCount);
      }
      return "";

    case "list": {
      const processedList = node.children.map((item, index) => {
        const itemText = processMarkdownNodeForCopy(item);
        if (node.ordered) {
          return `${index + 1}. ${itemText}`;
        } else {
          return `- ${itemText}`;
        }
      });
      return joinTextList(processedList, "\n");
    }

    case "listItem":
      return joinTextList(processMarkdownNodeListForCopy(node.children));

    case "blockquote":
      return joinTextList(processMarkdownNodeListForCopy(node.children));

    case "thematicBreak":
      return "---";

    case "table":
    case "tableRow":
    case "tableCell":
      return joinTextList(processMarkdownNodeListForCopy(node.children), " ");

    default:
      // For any unhandled node types, try to process children if they exist
      if ('children' in node && Array.isArray(node.children)) {
        return joinTextList(processMarkdownNodeListForCopy(node.children));
      }
      return "";
  }
}

/** @param {MdNode[]} nodeList */
function processMarkdownNodeListForCopy(nodeList) {
  return nodeList.map(node => processMarkdownNodeForCopy(node));
}

/**
 * @param {string[]} children
 * @param {string} separator
 */
function joinTextList(children, separator = "") {
  return children.filter(child => child !== "").join(separator);
}

// Keyed on message.message (the parts array) — GC'd automatically when sessions
// change and their message objects are released. Only populated for committed
// messages (those with _id). Streaming messages always bypass the cache.
const agentBubbleCache = new WeakMap()

function computeAgentBubbleData(message) {
  if (message._id) {
    const cached = agentBubbleCache.get(message.message)
    if (cached) return cached
  }

  const rawParts = normaliseChatMessageParts(message.message)
  const allRawCitations = getAllMessageCitations(rawParts)
  const { citations: messageCitations, indexMap } = deduplicateCitations(allRawCitations)
  const normalisedParts = rawParts.map(part => {
    if (part.type === "text") {
      return { ...part, data: { ...part.data, text: remapCitationsInText(part.data.text, indexMap) } }
    }
    if (part.type === "reasoning_step") {
      return { ...part, data: { ...part.data, content: remapCitationsInText(part.data.content, indexMap) } }
    }
    if (part.type === "reasoning_tag") {
      return { ...part, data: { ...part.data, content: remapCitationsInText(part.data.content, indexMap) } }
    }
    return part
  })
  const allMessageReports = getAllMessageReports(normalisedParts)
  const imageSearchResults = getAllMessageSearchImageResults(normalisedParts)
  const allGeneratedFiles = getAllMessageGeneratedFiles(normalisedParts)

  const result = { rawParts, messageCitations, indexMap, normalisedParts, allMessageReports, imageSearchResults, allGeneratedFiles }
  if (message._id) agentBubbleCache.set(message.message, result)
  return result
}

export {
  normaliseChatMessageParts,
  getAllMessageCitations,
  getAllMessageReports,
  getAllMessageSearchImageResults,
  getAllMessageGeneratedFiles,
  formatMarkdownForCopy,
  formatMarkdownForCopyHtml,
  deduplicateCitations,
  remapCitationsInText,
  computeAgentBubbleData,

  ChatMessagePartSchema,
  BASE_AGENT_ID,
  BASE_DEEPRESEARCH_AGENT_ID,
}
